import React, { FC, ReactElement, useState } from 'react'
import { CommentNotification } from './CommentNotification'
import { StandardNotification } from './StandardNotification'
import { NotificationSkeleton } from './Skeleton/NotificationSkeleton'
import { OrderNotificationProps, Notification } from './types'
import { Requests, ReactQuery } from '../../../api'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export const OrderNotification: FC<OrderNotificationProps> = ({ notification, status }): ReactElement => {
  const [fadeOut, setFadeOut] = useState(false)
  const notificationRequest = Requests.notification({ id: notification.id })
  const queryClient = useQueryClient()

  // Mutation for updating notification read status
  const updateNotificationMutation = useMutation({
    mutationFn: async ({ readStatus }: { readStatus: boolean }) => {
      const response = await ReactQuery.patchResource(notificationRequest, { read: readStatus })
      return response.data
    },
    onSuccess: (data, variables) => {
      // Update the notification in cache with the server response
      queryClient.setQueriesData({ queryKey: [notificationRequest.type], exact: false }, (old: any) => {
        if (!old) return old

        return {
          ...old,
          resources: old.resources?.map((n: Notification) => (n.id === notification.id ? data : n))
        }
      })

      // Invalidate unread count to refetch
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationsCount'] })

      if (variables.readStatus && status === 'unread') {
        setFadeOut(true)
      }
    }
  })

  const changeReadStatus = (e: Event | null, notification: Notification, readStatus: boolean) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    updateNotificationMutation.mutate({ readStatus })
  }

  const goToOrder = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, notification: Notification) => {
    if (event.metaKey || event.ctrlKey) {
      changeReadStatus(null, notification, true)
      return
    }

    event.preventDefault()
    updateNotificationMutation.mutate(
      { readStatus: true },
      {
        onSuccess: () => {
          window.location.href = notification.orderPath
        }
      }
    )
  }

  if (fadeOut) {
    return <></>
  }

  if (updateNotificationMutation.isLoading) {
    return <NotificationSkeleton />
  }

  let notificationComponent

  if (notification.classification === 'order_comments') {
    notificationComponent = <CommentNotification notification={notification} changeReadStatus={changeReadStatus} />
  } else {
    notificationComponent = <StandardNotification notification={notification} changeReadStatus={changeReadStatus} />
  }

  return (
    <a href={notification.orderPath} onClick={(e) => goToOrder(e, notification)}>
      {notificationComponent}
    </a>
  )
}
